import type { BanRuleMatchMode, BannedEmailSource, BannedIpSource } from "@shopexperts/legacy-api";

const dateTimeFormatter = new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
});

export function formatBanDate(value: string | null | undefined, emptyLabel = "Never"): string {
    if (!value) return emptyLabel;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "Unknown";
    return dateTimeFormatter.format(parsed);
}

export function formatHits(count: number): string {
    if (count === 0) return "No hits";
    if (count === 1) return "1 hit";
    return `${count.toLocaleString()} hits`;
}

/**
 * Rule types come from the server-side matcher registry, so an unknown type is
 * a new matcher rather than an error — humanize it instead of hiding it.
 */
export function formatRuleType(type: string): string {
    if (type === "project_text") return "Project text";
    return type
        .split("_")
        .filter(Boolean)
        .map((part, index) => (index === 0 ? part.charAt(0).toUpperCase() + part.slice(1) : part))
        .join(" ");
}

export function formatMatchMode(mode: BanRuleMatchMode): string {
    return mode === "substring" ? "Substring" : "Whole word";
}

export function describeMatchMode(mode: BanRuleMatchMode): string {
    return mode === "substring"
        ? "Terms match anywhere in the text — for URLs, handles and fragments."
        : "Terms match on word boundaries — “seo” does not match “seasonal”.";
}

export function formatSource(source: BannedEmailSource | BannedIpSource): string {
    switch (source) {
        case "rule":
            return "Rule";
        case "manual":
            return "Manual";
        case "repeat":
            return "Repeat";
        default:
            return source;
    }
}

export type ChipVariant = "success" | "neutral" | "warn" | "outline";

export function sourceChipVariant(source: BannedEmailSource | BannedIpSource): ChipVariant {
    switch (source) {
        case "rule":
            return "warn";
        case "repeat":
            return "outline";
        default:
            return "neutral";
    }
}

/** `{ source: 'public_draft_lead', draft_lead_id: 88 }` → readable label/value rows. */
export function contextEntries(
    context: Record<string, unknown> | null | undefined,
): { label: string; value: string }[] {
    if (!context) return [];
    return Object.entries(context).map(([key, value]) => ({
        label: key.replace(/_/g, " "),
        value: value == null
            ? "—"
            : typeof value === "object"
                ? JSON.stringify(value)
                : String(value),
    }));
}

/** The IP registry is export-only — the API never rejects a request because of it. */
export function buildIpRegistryExport(rows: { ip_address: string }[]): string {
    return rows.map((row) => row.ip_address).join("\n");
}
